import { Button } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { ExportIcon, FilterIcon } from 'BreetAssets';
import { DropdownButton, type IDropdownOption, PageHeader, SearchInput } from 'BreetComponents';
import { getQueryParamValue, updateUrlSearchParams } from 'BreetHelpers';
import { useModalApi, usePermissions, useWindowSize } from 'BreetHooks';
import debounce from 'lodash/debounce';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { type IDataTableHeader, type TableHeaderFilterItemOptions } from './DataTable.helpers';

export const DataTableHeader = ({
	filterProps,
	exportProps,
	filterList,
	pageTitle,
	searchProps,
	onSelectedFilterChange,
	isLoading,
	onRefresh,
	otherBtnProps,
	tip,
	counter,
	isFilter,
	activeFilters,
	onClearFilters,
}: IDataTableHeader) => {
	const filterCount = activeFilters?.length ?? 0;
	const isFilterActive = filterCount > 0 || !!isFilter;
	const [selectedFilters, setSelectedFilters] = useState<Record<string, TableHeaderFilterItemOptions | undefined>>({});

	const { handleOpenModal } = useModalApi();

	const windowSizeProps = useWindowSize();

	const { hasPermission } = usePermissions();

	const [searchValue, setSearchValue] = useState<string>('');

	const debouncedSearch = useMemo(
		() =>
			debounce((onSearch: (data: Record<string, string>) => void, searchKey: string | number, value: string) => {
				onSearch({ [String(searchKey)]: value });
			}, 300),
		[]
	);

	useEffect(() => {
		if (onSelectedFilterChange) onSelectedFilterChange(selectedFilters);
	}, [onSelectedFilterChange, selectedFilters]);

	useEffect(() => {
		filterList?.forEach((filter) => {
			const encodedFilterOptions = getQueryParamValue(filter.id);
			if (encodedFilterOptions) {
				const decodedFilterOptions = JSON.parse(decodeURIComponent(encodedFilterOptions)) as TableHeaderFilterItemOptions;
				setSelectedFilters({ [filter.id]: decodedFilterOptions });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleSelectedFilters = useCallback((filterKey: string, selectedItem: IDropdownOption) => {
		setSelectedFilters((prevData) => {
			const { [filterKey]: existingEntry, ...newFilters } = prevData;

			if (JSON.stringify(existingEntry) === JSON.stringify(selectedItem.value)) {
				return { ...newFilters, [filterKey]: undefined };
			}

			return { ...newFilters, [filterKey]: selectedItem.value } as Record<string, TableHeaderFilterItemOptions | undefined>;
		});
	}, []);

	const handleOptionClick = useCallback(
		(filterKey: string) => (selectedItem?: IDropdownOption) => {
			if (!selectedItem) return;
			toggleSelectedFilters(filterKey, selectedItem);
			updateUrlSearchParams({ addParams: { [filterKey]: encodeURIComponent(JSON.stringify(selectedItem.value)) } });
		},
		[toggleSelectedFilters]
	);

	const onHandleSearch = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchValue(e.target.value);

			if (searchProps?.handleReqSearch) {
				debouncedSearch(searchProps.handleReqSearch, searchProps.searchKey ?? 'search', e.target.value);
			}
		},
		[searchProps, debouncedSearch]
	);

	const exportConfig = typeof exportProps === 'object' ? exportProps : undefined;

	return (
		<>
			<div className='datatable_header'>
				<PageHeader
					pageTitle={pageTitle}
					isLoading={isLoading}
					onRefresh={onRefresh}
					counter={counter}
					tip={tip}
				/>
				<div className='datatable_headersearchfilter'>
					{!!searchProps && (
						<SearchInput
							widthType='full'
							id=''
							onChange={onHandleSearch}
							placeholder={searchProps.placeHolder}
							value={searchValue}
						/>
					)}
					{!!filterProps && (
						<DropdownButton
							label={filterCount > 0 ? `${filterProps.label ?? 'Filter'} · ${filterCount}` : (filterProps.label ?? 'Filter')}
							leadingIcon={filterProps.leadingIcon}
							trailingIcon={filterProps.trailingIcon ?? <FilterIcon className='svg-dark' />}
							variant={filterProps.variant ?? 'outline'}
							className={classNameMerger(filterProps.className, isFilterActive && 'datatable_filterbtn--active')}
							dropDownComponent={filterProps.dropDownComponent}
							switchMobileWidth={filterProps.switchMobileWidth ?? 768}
							switchLeadingMobileIcon={filterProps.switchLeadingMobileIcon ?? true}
							onChange={handleOptionClick(filterProps.filterKey ?? '')}
						/>
					)}

					{otherBtnProps?.map((btnItem) => (
						<Button
							key={btnItem.label}
							onClick={btnItem.onClick}
							leadingIcon={btnItem.leadingIcon}
							trailingIcon={btnItem.trailingIcon}
							className={btnItem.className}
						>
							{windowSizeProps.width < 768 ? '' : btnItem.label}
						</Button>
					))}

					{exportProps && hasPermission('export') && (
						<Button
							key='export'
							onClick={handleOpenModal({ id: 'fileExport', variant: 'right-sheet' })}
							variant={exportConfig?.variant ?? 'tertiary'}
							trailingIcon={
								exportConfig?.trailingIcon ?? (
									<ExportIcon
										variant='table'
										className='svg-dark'
									/>
								)
							}
							className='tertiary-btn theme-background-none bg-primary-400'
						>
							{windowSizeProps.width < 768 ? '' : (exportConfig?.label ?? 'Export')}
						</Button>
					)}
				</div>
			</div>

			{!!activeFilters?.length && (
				<div className='datatable_activefilters'>
					{activeFilters.map((chip) => (
						<button
							key={chip.id}
							type='button'
							className='datatable_filterchip'
							onClick={chip.onRemove}
							aria-label={`Remove filter: ${chip.label}`}
						>
							<span className='datatable_filterchip_label'>{chip.label}</span>
							<span
								aria-hidden='true'
								className='datatable_filterchip_remove'
							>
								✕
							</span>
						</button>
					))}

					{onClearFilters && (
						<button
							type='button'
							className='datatable_clearfilters'
							onClick={onClearFilters}
						>
							Clear all
						</button>
					)}
				</div>
			)}
		</>
	);
};
